/**
 * A card's cell in a table row a pending tracked change removes.
 *
 * WHY
 *   A card applied on its own into a cell of a row another card had already struck
 *   wrote into a row that goes on Accept All, and nothing told the user (page finding
 *   #4, 2026-10-16). Product wants the conflict shown and never the write refused: the
 *   pending card carries a status label of its own, specific to the row and naming the
 *   card that struck it when the tracker knows it, and the write goes through under
 *   that label. Once applied it reads Applied, as does the card that struck the row.
 *
 * The one reading of a struck row is the row's own revision mark (`w:trPr/w:del`),
 * which `OoxmlRange.documentParagraphs` reads onto every paragraph's cell path as
 * `rowRevision`. The tracker reads it off the watched page's table reads
 * (`livePageSnapshotOf`); a card's own Apply reads it once more off the body as it
 * stands when the card is written (`removedRowKeysOfBookmarksInDocument`).
 */
import { DocumentComments } from "@common/utils/redline/ooxml/document-comments";
import { OoxmlNumbering } from "@common/utils/redline/ooxml/ooxml-numbering";
import { OoxmlOutlineLevels } from "@common/utils/redline/ooxml/ooxml-outline-levels";
import { OoxmlRange } from "@common/utils/redline/ooxml-range";
import type { DocumentParagraph } from "@common/utils/redline/wire/document-paragraph";
import type { RedlineTextProposal } from "@common/utils/redline/wire/redline-payload";

import { withWordContextRead } from "./document";

/** What the label names when no card of the tracker is known to have struck the row: a change of the document's own. */
export const ROW_REMOVED_BY_A_PENDING_CHANGE = "a pending change";

/** One card the tracker knows struck rows and that was applied: its number, as the label names it, and the text card carrying the row deletions. */
export interface RowStrikingCard {
    cardNumber: number;
    textRedline: RedlineTextProposal;
}

/**
 * The row of `paragraph`'s cell when a pending change removes that row, as a key
 * within one read: the table's number in the read and the row's number in the table.
 *
 * CASES
 *   - outside every table                          -> null
 *   - its innermost cell's row stands              -> null
 *   - its innermost cell's row is tracked-deleted  -> "tableNumber:rowNumber"
 *
 * EXAMPLE
 *   the answer cell of row 2 of the read's first table, the row struck -> "1:2"
 */
export function removedRowKeyOf(paragraph: DocumentParagraph): string | null {
    /** The last entry of the path is the cell the paragraph is written in; a nested table's paragraph names its inner cell's row. */
    const innermostCell = paragraph.tableCellPath[paragraph.tableCellPath.length - 1];
    if (innermostCell === undefined || innermostCell.rowRevision !== "deleted") return null;
    return `${innermostCell.tableNumber}:${innermostCell.rowNumber}`;
}

/**
 * The removed row of each bookmark whose paragraph sits in one, read off one read of
 * Word: a table's package, the row Word returns for a cell paragraph, or the body.
 *
 * EXAMPLE
 *   the Ex. 3 row struck, read for ["_fee_cell"] -> Map { "_fee_cell" => "1:1" }
 *   the same row standing                        -> Map {}
 *
 * NULL
 *   A bookmark the read does not hold has no entry, like one whose row stands.
 */
export function removedRowKeysOfBookmarks(readOoxml: string, bookmarkNames: readonly string[]): Map<string, string> {
    const paragraphs = new OoxmlRange(readOoxml).documentParagraphs(DocumentComments.none(), OoxmlNumbering.none(), OoxmlOutlineLevels.none());
    const removedRowKeyByBookmark = new Map<string, string>();
    for (const bookmarkName of bookmarkNames) {
        const paragraph = paragraphs.find((candidate) => candidate.bookmarkNames.includes(bookmarkName));
        const removedRowKey = paragraph === undefined ? null : removedRowKeyOf(paragraph);
        if (removedRowKey !== null) removedRowKeyByBookmark.set(bookmarkName, removedRowKey);
    }
    return removedRowKeyByBookmark;
}

/**
 * The removed rows of the bookmarks as the body stands now: one read of Word, made
 * when a card is applied on its own, so the write goes through under the label the
 * row calls for whatever the watched page last showed.
 *
 * FALLBACK
 *   A read Word refuses answers no removed row: the label is advisory, and a card's
 *   write is never failed over it. The watched page labels the card on its next read.
 */
export async function removedRowKeysOfBookmarksInDocument(bookmarkNames: readonly string[]): Promise<Map<string, string>> {
    try {
        return await withWordContextRead(async (context) => {
            const bodyOoxml = context.document.body.getOoxml();
            await context.sync();
            return removedRowKeysOfBookmarks(bodyOoxml.value, bookmarkNames);
        });
    } catch {
        return new Map();
    }
}

/**
 * Who removed the row the card's cells sit in, for the card's label.
 *
 * CASES
 *   - the card is no text card, or no cell of it sits in a removed row -> undefined: no label
 *   - a card the tracker knows struck that row: a row deletion it carries
 *     names a cell of the same row, or it lists the card's cell among the
 *     cells of the row it strikes                                      -> "#5", that card's number
 *   - the row is removed and no such card is known                      -> "a pending change"
 *
 * EXAMPLE
 *   the card writes "_fee_cell", in removed row "T1:1:1"; card #5 deletes the row of "_ex3_cell", also "T1:1:1" -> "#5"
 */
export function rowRemovedByOf(textRedline: RedlineTextProposal | null, removedRowKeyByBookmark: ReadonlyMap<string, string>, strikingCards: readonly RowStrikingCard[]): string | undefined {
    if (textRedline === null) return undefined;
    const cardBookmarks = textRedline.paragraphs.flatMap((paragraph) => (paragraph.bookmark === null ? [] : [paragraph.bookmark]));
    const removedRowKeys = new Set(cardBookmarks.flatMap((bookmark) => {
        const removedRowKey = removedRowKeyByBookmark.get(bookmark);
        return removedRowKey === undefined ? [] : [removedRowKey];
    }));
    if (removedRowKeys.size === 0) return undefined;
    const strikerOfTheRow = strikingCards.find((strikingCard) =>
        strikingCard.textRedline.tableChanges.rowDeletions.some((deletion) => removedRowKeys.has(removedRowKeyByBookmark.get(deletion.rowOfBookmark) ?? ""))
        || strikingCard.textRedline.paragraphs.some((paragraph) => paragraph.bookmark !== null && cardBookmarks.includes(paragraph.bookmark)));
    return strikerOfTheRow === undefined ? ROW_REMOVED_BY_A_PENDING_CHANGE : `#${strikerOfTheRow.cardNumber}`;
}

/**
 * The status label of a pending card whose cell sits in a row a pending change
 * removes: what applying it does.
 *
 * WHY only a pending card
 *   An applied card reads Applied, whoever removes its row, and so does the card
 *   that struck the row; the label was too much once the write was done.
 *
 * WHY this short
 *   It sits on one line beside the card's number in every view; the sentence it
 *   replaced, 72 characters, wrapped to two.
 *
 * EXAMPLE
 *   "#5"                              -> "Removed if #5 is accepted"
 *   ROW_REMOVED_BY_A_PENDING_CHANGE   -> "Removed if a pending change is accepted"
 */
export function rowRemovedLabelOf(removedBy: string): string {
    return `Removed if ${removedBy} is accepted`;
}
